
import React, { useState } from "react";
import { useTodosLive } from "../../hooks/useTodosLive";
import { db } from "../../../firebaseClient";
import { computeDashboardSummary, TodoItem } from "../../services/todosSummary";

export function DashboardTodosDebugTab() {
  const { 
      liveStatus, 
      liveMsg, 
      data: dashboardData, 
      rawDocs, 
      lastSnapshotAt, 
      snapshotMeta, 
      retryCount,
      isActive,
      handleManualReconnect, 
      startListener, 
      stopListener 
  } = useTodosLive();

  const [probeResult, setProbeResult] = useState<any>(null);
  const [probeLoading, setProbeLoading] = useState(false);
  const [showAllTodos, setShowAllTodos] = useState(false);

  // 1. Server Probe Logic
  const handleProbe = async () => {
      setProbeLoading(true);
      setProbeResult(null);
      const start = Date.now();
      try {
          // Compat style
          const q = db.collection('todos').limit(1);
          const snap = await q.get({ source: 'server' });
          const duration = Date.now() - start;
          setProbeResult({
              success: true,
              duration: `${duration}ms`,
              docsFound: snap.size,
              fromCache: snap.metadata.fromCache,
              timestamp: new Date().toLocaleTimeString()
          });
      } catch (e: any) {
          setProbeResult({
              success: false,
              duration: `${Date.now() - start}ms`,
              error: e.message,
              code: e.code
          });
      } finally {
          setProbeLoading(false);
      }
  };

  // 2. Compute Consistency
  // We re-compute using the rawDocs from the hook to see if logic matches dashboardData
  // Since dashboardData in hook comes from the same logic, we expect them to match unless hook state is stale.
  const openTodos = rawDocs.filter(t => t.todoStatus === 'OPEN');
  const computedSummary = computeDashboardSummary(openTodos);
  
  // Diff checker
  const renderDiff = (userKey: 'kevin'|'lisa'|'irene', field: 'contracts'|'seals'|'others') => {
      const dashVal = dashboardData?.users.find(u => u.userKey === userKey)?.counts[field] || 0;
      const debugVal = computedSummary.users.find(u => u.userKey === userKey)?.counts[field] || 0;
      
      if (dashVal === debugVal) return <span style={{color:'var(--ok)'}}>{dashVal}</span>;
      return <span style={{color:'var(--danger)', fontWeight:'bold'}}>{dashVal} vs {debugVal}</span>;
  };

  const getStatusColor = (s: string) => {
      if (s === 'connected') return 'var(--ok)';
      if (s === 'connecting') return '#fbbf24';
      if (s === 'offline' || s === 'error') return 'var(--danger)';
      return '#888';
  };

  return (
    <div style={{display:'flex', flexDirection:'column', gap: 24}}>
        
        {/* A. Listener Health */}
        <div className="section">
            <h3 className="h1" style={{color: '#60a5fa', marginBottom: 12}}>A. 監聽連線健康度 (Listener Health)</h3>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12, marginBottom: 16}}>
                <InfoCard label="Live Status" value={liveStatus} color={getStatusColor(liveStatus)} />
                <InfoCard label="Active Listener" value={isActive ? "Yes" : "No"} color={isActive ? 'var(--ok)' : 'var(--danger)'} />
                <InfoCard label="Retry Attempts" value={retryCount} color={retryCount > 0 ? '#fbbf24' : '#ccc'} />
                <InfoCard label="Last Snapshot" value={lastSnapshotAt ? lastSnapshotAt.toLocaleTimeString() : '-'} />
                <InfoCard label="From Cache" value={snapshotMeta?.fromCache ? "YES" : "NO"} color={snapshotMeta?.fromCache ? '#fbbf24' : 'var(--ok)'} />
                <InfoCard label="Total Raw Docs" value={rawDocs.length} />
            </div>
            
            <div style={{background:'rgba(255,255,255,0.05)', padding:8, borderRadius:6, marginBottom:12, color: getStatusColor(liveStatus)}}>
                💬 {liveMsg}
            </div>

            <div style={{display:'flex', gap:10}}>
                <button className="btn" style={{width:'auto', background:'#3b82f6'}} onClick={startListener} disabled={isActive}>啟動監聽</button>
                <button className="btn" style={{width:'auto', background:'transparent', border:'1px solid var(--danger)', color:'var(--danger)'}} onClick={stopListener} disabled={!isActive}>停止監聽</button>
                <button className="btn" style={{width:'auto', background:'#fff', color:'#000'}} onClick={handleManualReconnect} disabled={liveStatus === 'connecting'}>手動重新連線</button>
            </div>
        </div>

        {/* B. Server Probe */}
        <div className="section">
             <h3 className="h1" style={{color: '#a78bfa', marginBottom: 12}}>B. 伺服器探測 (Server Probe)</h3>
             <div style={{display:'flex', alignItems:'center', gap: 16}}>
                 <button className="btn" style={{width:'auto'}} onClick={handleProbe} disabled={probeLoading}>
                     {probeLoading ? "Probing..." : "⚡ Probe Server (limit 1)"}
                 </button>
                 {probeResult && (
                     <div style={{fontSize:13, fontFamily:'monospace', color: probeResult.success ? 'var(--ok)' : 'var(--danger)'}}>
                         {JSON.stringify(probeResult)}
                     </div>
                 )}
             </div>
        </div>

        {/* C. Consistency Check */}
        <div className="section">
             <h3 className="h1" style={{color: '#fbbf24', marginBottom: 12}}>C. 儀錶板三卡統計一致性</h3>
             
             {/* Cards Preview */}
             <div style={{display:'flex', gap:16, overflowX:'auto', marginBottom: 16, paddingBottom:8}}>
                 {computedSummary.users.map(u => (
                     <div key={u.userKey} style={{minWidth: 200, padding: 12, background:'rgba(255,255,255,0.03)', border:'1px solid #444', borderRadius:8}}>
                         <div style={{fontWeight:'bold', marginBottom:8}}>{u.displayName}</div>
                         <div style={{fontSize:12, color:'#aaa'}}>
                             合約: {u.counts.contracts} | 用印: {u.counts.seals} | 其他: {u.counts.others}
                         </div>
                     </div>
                 ))}
             </div>

             {/* Diff Table */}
             <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
                 <thead>
                     <tr style={{background:'#222', textAlign:'left'}}>
                         <th style={{padding:8}}>User</th>
                         <th style={{padding:8}}>Contracts (Dash vs Debug)</th>
                         <th style={{padding:8}}>Seals (Dash vs Debug)</th>
                         <th style={{padding:8}}>Others (Dash vs Debug)</th>
                     </tr>
                 </thead>
                 <tbody>
                     {(['kevin','lisa','irene'] as const).map(key => (
                         <tr key={key} style={{borderBottom:'1px solid #333'}}>
                             <td style={{padding:8, fontWeight:'bold'}}>{key.toUpperCase()}</td>
                             <td style={{padding:8}}>{renderDiff(key, 'contracts')}</td>
                             <td style={{padding:8}}>{renderDiff(key, 'seals')}</td>
                             <td style={{padding:8}}>{renderDiff(key, 'others')}</td>
                         </tr>
                     ))}
                 </tbody>
             </table>
        </div>

        {/* D. Raw Data */}
        <div className="section">
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12}}>
                 <h3 className="h1" style={{color: '#34d399', margin:0}}>D. 原始 Todo 資料 ({rawDocs.length})</h3>
                 <label style={{display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer'}}>
                     <input type="checkbox" checked={showAllTodos} onChange={e => setShowAllTodos(e.target.checked)} />
                     顯示所有狀態 (包含 CLOSED)
                 </label>
            </div>

            <div style={{maxHeight: 400, overflowY:'auto', border:'1px solid #333', borderRadius:8}}>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:12}}>
                    <thead style={{background:'#222', position:'sticky', top:0}}>
                        <tr style={{textAlign:'left', color:'#aaa'}}>
                            <th style={{padding:8}}>Updated</th>
                            <th style={{padding:8}}>Module</th>
                            <th style={{padding:8}}>DocNo</th>
                            <th style={{padding:8}}>Title</th>
                            <th style={{padding:8}}>Todo Status</th>
                            <th style={{padding:8}}>Target User</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rawDocs.filter(d => showAllTodos || d.todoStatus === 'OPEN').map(d => {
                            // Re-calculate target user logic for visibility
                            const owner = (d.currentOwnerName || d.assigneeName || "").toLowerCase();
                            let target = "Kevin";
                            if (owner.includes("lisa")) target = "Lisa";
                            else if (owner.includes("irene")) target = "Irene";
                            
                            return (
                                <tr key={d.id} style={{borderBottom:'1px solid #333', background: d.todoStatus === 'CLOSED' ? 'rgba(0,0,0,0.3)' : 'transparent'}}>
                                    <td style={{padding:8, fontFamily:'monospace', color:'#666'}}>
                                        {d.updatedAt?.toDate ? d.updatedAt.toDate().toLocaleTimeString() : '-'}
                                    </td>
                                    <td style={{padding:8}}>{d.module}</td>
                                    <td style={{padding:8, color:'#fbbf24'}}>{d.docNo}</td>
                                    <td style={{padding:8}}>{d.title}</td>
                                    <td style={{padding:8, color: d.todoStatus === 'OPEN' ? 'var(--ok)' : '#666', fontWeight:'bold'}}>{d.todoStatus}</td>
                                    <td style={{padding:8, color:'#a78bfa'}}>{target}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
  );
}

function InfoCard({label, value, color}: any) {
    return (
        <div style={{background:'#222', padding:12, borderRadius:6, border:'1px solid #333'}}>
            <div style={{fontSize:11, color:'#888', marginBottom:4}}>{label}</div>
            <div style={{fontSize:16, fontWeight:'bold', color: color || '#fff'}}>{value}</div>
        </div>
    );
}
